// W15-1 — THE ORDINAL LAW. ONE FUNCTION, AND HER RULING IS THAT IT HAS NO EXCEPTIONS.
//
// **HER CAPTURE: "3th house" AND "1th house".** Both came off one line in the astrocartography
// room's "what moves" list, which glued `th` onto a raw number:
//
//     your {c.from}th house at home, your {c.to}th here
//
// **AND THE LAW ALREADY EXISTED THREE TIMES, WHICH IS EXACTLY WHY A FOURTH SITE COULD BE WRONG:**
//
//   1. `src/lib/format.ts` `ordinalLabel`      — correct, carries the 11/12/13 rule.
//   2. `server/index.js` `ordinalHouse`        — correct, an independent copy of the same rule.
//   3. `server/chart-houses.js` `ordinal`      — a TABLE of 1..12 that falls back to the BARE
//                                                number, so `ordinal(13)` was "13", not "13th".
//                                                A silent degradation rather than an error.
//   4. `AcgViews.tsx`                          — no function at all.
//
// Four implementations, three behaviours, and the one her members read was the one with none. R31's
// answer applies and it is the reason this file exists: **DERIVE, do not gate a duplicate.** There is
// one law now and the other sites import it, so the crossing stops existing rather than being watched.
//
// It lives in src/*.js and NOT src/lib/*.ts because CLAUDE.md draws exactly that line: src/lib is
// CLIENT-ONLY, and this law is composed on BOTH sides (server/index.js and server/chart-houses.js each
// had their own copy). It imports NOTHING, the entry requirement that lets a .mjs gate load and RUN it.

// The English rule, in full. 11, 12 and 13 are the exceptions that every naive implementation gets
// wrong, and they are exceptions in EVERY hundred: 111th, 112th, 113th, 211th and so on.
export function ordinalSuffix(n) {
  const num = Math.trunc(Math.abs(Number(n)));
  if (!Number.isFinite(num)) return "th";
  const rem100 = num % 100;
  if (rem100 >= 11 && rem100 <= 13) return "th";
  return ["th", "st", "nd", "rd"][num % 10] || "th";
}

// The number wearing its suffix: 1 -> "1st", 3 -> "3rd", 13 -> "13th", 21 -> "21st".
//
// A non-number NEVER silently becomes "NaNth". It returns the empty string, and the callers that
// compose a sentence around it are held to checking — R17's lesson, that the receiving side's
// default must not be silence dressed as an answer.
export function ordinal(n) {
  // `Number(null)` is 0 and `Number("")` is 0, so a bare Number() check would turn a MISSING house
  // into a confident "0th". Caught by this module's own fixture on its first run, which is the
  // argument for putting a law where a gate can RUN it.
  if (n === null || n === undefined || String(n).trim() === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  const whole = Math.trunc(num);
  return `${whole}${ordinalSuffix(whole)}`;
}

// The house form, which is what almost every caller on this site actually wants: "your 3rd house".
// Kept beside the general rule rather than in a room, because five rooms compose this phrase and the
// last time it lived at a call site it read "3th".
export function ordinalHouse(n) {
  const o = ordinal(n);
  return o ? `${o} house` : "";
}
